// src/hooks/useUserAddresses.jsx
import { useState, useEffect, useCallback } from "react";
import { useSupaContext } from "../contexts/SupaContext";
import { useSalesContext } from "../contexts/SalesContext";
import { useAddress } from "./useAddress";
import primaryCoveragePolygon from "../componets/pages/coverageComponents/perimeter.json";
import supabase from "../SupaCredentials";
import toast from "react-hot-toast";

export const useUserAddresses = () => {
  const { user } = useSupaContext();
  const {
    setSelectedAddress,
    setSelectedCoordinates,
    setAddressDetails,
    setImageAddress,
    setDeliveryFee,
    selectedAddressId,
    setSelectedAddressId,
    setSelectedZone,
    selelectedZone,
    setIsChangingAddress
  } = useSalesContext();

  // Siempre llamamos al hook, independientemente de si hay usuario o no
  // Esto cumple con las reglas de hooks de React
  const addressUtils = useAddress(primaryCoveragePolygon);

  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSelectingAddress, setIsSelectingAddress] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(true);

  // Nueva función para deseleccionar la dirección actual
  const deselectAddress = () => {
    console.log("useUserAddresses: Deseleccionando dirección");
    setSelectedAddressId(null);
    setSelectedAddress("");
    setSelectedCoordinates(null);
    setAddressDetails("");
    setImageAddress(null);
    setDeliveryFee(0);

    // Forzar recálculo del total después de deseleccionar
    // Si tienes acceso a setNewOrderTotal aquí, podrías llamarlo directamente
  };

  // Función de utilidad para validación segura de ubicación
  const safeValidateLocation = (coordinates, polygon) => {
    try {
      // Verificar si addressUtils existe y tiene la función validateLocation
      if (
        !addressUtils ||
        typeof addressUtils.validateLocation !== "function"
      ) {
        return false;
      }

      // Verificar validez de coordenadas y polígono
      if (!coordinates?.lat || !coordinates?.lng || !polygon) {
        return false;
      }

      // Ejecutar la validación real
      return addressUtils.validateLocation(coordinates, polygon);
    } catch (error) {
      console.error("Error en validación de ubicación:", error);
      return false;
    }
  };

  // Fetches user's saved addresses
  const fetchUserAddresses = async () => {
    if (!user?.id) {
      setAddresses([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("addresses")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setAddresses(data || []);

      // If we have addresses and none is selected, select the first one
      if (data?.length > 0 && !selectedAddressId) {
        selectAddress(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast.error("No pudimos cargar tus direcciones guardadas");
    } finally {
      setIsLoading(false);
    }
  };

  // En useUserAddresses.jsx - Función selectAddress (aproximadamente línea 30-100)
  // BUSCA y MODIFICA solo esta parte:

  const selectAddress = async (addressId) => {
    // No hacer nada si no hay usuario o direcciones
    if (!user?.id || addresses.length === 0) return;

    console.log(`useUserAddresses: Seleccionando dirección ${addressId}`);
    setIsSelectingAddress(true); // Iniciar indicador de carga

    try {
      const address = addresses.find((addr) => addr.id === addressId);
      if (!address) {
        console.error("Dirección no encontrada:", addressId);
        setIsSelectingAddress(false);
        return;
      }

      // Actualizar ID seleccionado y todos los estados relevantes de manera atómica
      setSelectedAddressId(addressId);

      // Actualizar estado en SalesContext
      setSelectedAddress(address.address || "");

      if (address.latitude && address.longitude) {
        setSelectedCoordinates({
          lat: address.latitude,
          lng: address.longitude,
        });

        if (addressUtils && addressUtils.validateLocation) {
          const coords = { lat: address.latitude, lng: address.longitude };
          const validationResult = addressUtils.validateLocation(coords);

          if (validationResult) {
            const { zone, isValid } = validationResult;

            // Establecer la zona en SalesContext
            if (setSelectedZone) {
              setSelectedZone(zone);
            }

            // ELIMINAR ESTAS LÍNEAS DUPLICADAS:
            // const deliveryFee = zone === 1 ? 0 : zone === 2 ? 3500 : 7000;
            // setDeliveryFee(deliveryFee);

            // El cálculo del delivery fee se hace más abajo con la lógica completa
            console.log(`Dirección seleccionada - Zona: ${zone}`);
          }
        }
      }

      if (address.details) {
        setAddressDetails(address.details);
      }

      // Calcular tarifa de envío basada en ubicación
      await new Promise((resolve) => {
        // Determinar tarifa basada en zona
        let deliveryCharge = 0;

        if (address.latitude && address.longitude) {
          const coordinates = {
            lat: address.latitude,
            lng: address.longitude,
          };

          try {
            // Usar validateLocation si está disponible
            if (addressUtils?.validateLocation) {
              const { isInPrimary, zone } =
                addressUtils.validateLocation(coordinates);

              // CAMBIAR esta lógica para usar las zonas correctamente
              if (zone === 1) {
                deliveryCharge = 0;
              } else if (zone === 2) {
                deliveryCharge = 3500;
              } else if (zone === 3) {
                deliveryCharge = 7000;
              } else {
                // Si no está en ninguna zona válida
                deliveryCharge = 7000;
              }
            }
          } catch (err) {
            console.error("Error validando ubicación:", err);
          }
        }

        // Establecer tarifa - Esta es la única vez que se debe establecer
        setDeliveryFee(deliveryCharge);

        // Cargar imagen si existe
        if (address.image_url) {
          fetch(address.image_url)
            .then((response) => response.blob())
            .then((blob) => {
              setImageAddress(blob);
              resolve();
            })
            .catch((err) => {
              console.error("Error cargando imagen:", err);
              setImageAddress(null);
              resolve();
            });
        } else {
          resolve();
        }
      });

      console.log("Dirección seleccionada exitosamente:", addressId);
    } catch (error) {
      console.error("Error seleccionando dirección:", error);
      toast.error("Error al seleccionar la dirección");
    } finally {
      setIsSelectingAddress(false);
    }
  };

  // Fetches static map image for a selected address
  const fetchMapImage = async (lat, lng) => {
    if (!lat || !lng) return;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );

      if (!response.ok) {
        throw new Error(`Error fetching map image: ${response.status}`);
      }

      const blob = await response.blob();
      // Considera esto
      if (blob instanceof Blob) {
        // Si necesitas URL en el contexto
        const url = URL.createObjectURL(blob);
        setImageAddress(url);

        // Opcionalmente, limpia cuando ya no se necesite
        return () => URL.revokeObjectURL(url);
      } else if (typeof blob === "string") {
        // Si ya es una URL
        setImageAddress(blob);
      } else {
        // Si no es válido
        setImageAddress(null);
      }
    } catch (error) {
      console.error("Error fetching map image:", error);
      // No establecer imagen si hay error
    }
  };

  // Creates a new address
  const createAddress = async (addressData) => {
    if (!user?.id) return null;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("addresses")
        .insert([
          {
            user_id: user.id,
            address: addressData.address,
            city: addressData.city || "Barranquilla",
            state: addressData.state || "ATL",
            country: addressData.country || "Colombia",
            latitude: addressData.latitude,
            longitude: addressData.longitude,
            details: addressData.details,
            pre_registration: false,
            image_url: addressData.image_url, // Nueva propiedad
          },
        ])
        .select();

      if (error) throw error;

      // Forzar recarga de direcciones después de crear una nueva
      setShouldRefetch(true);
      // Recargar inmediatamente para mostrar la nueva dirección
      await fetchUserAddresses();

      if (data?.[0]?.id) {
        selectAddress(data[0].id);

        // Show success message
        toast.success("¡Dirección guardada correctamente!", {
          duration: 3000,
          position: "top-center",
        });
      }

      setIsCreatingNew(false);
      setHasUnsavedChanges(false);
      return data?.[0] || null;
    } catch (error) {
      console.error("Error creating address:", error);
      toast.error("Hubo un problema al guardar la dirección");
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Deletes an address
  const deleteAddress = async (addressId) => {
    if (!user?.id || !addressId) return false;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("addresses")
        .delete()
        .eq("id", addressId);

      if (error) throw error;

      // Refresh the addresses list
      await fetchUserAddresses();

      // If the deleted address was selected, reset selection
      if (selectedAddressId === addressId) {
        setSelectedAddressId(null);
        setSelectedAddress("");
        setSelectedCoordinates(null);
        setAddressDetails("");
        setImageAddress(null);
        setDeliveryFee(0);
      }

      return true;
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("No pudimos eliminar esta dirección");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Mejorar startCreatingAddress para asegurar que se puede llamar
  const startCreatingAddress = useCallback(() => {
    console.log("startCreatingAddress called");
    if (!user?.id) {
      toast.error("Debes iniciar sesión para crear una dirección");
      return;
    }

    // Deseleccionar la dirección actual antes de cambiar a la vista de crear
    deselectAddress();

    setIsCreatingNew(true);
    setHasUnsavedChanges(false);
  }, [user?.id]);

  // Cancel creating a new address
  const cancelCreatingAddress = () => {
    if (hasUnsavedChanges) {
      // Show confirmation dialog
      const confirmed = window.confirm(
        "Tienes cambios sin guardar. ¿Estás seguro de que quieres cancelar?"
      );
      if (!confirmed) return;
    }

    setIsCreatingNew(false);
    setHasUnsavedChanges(false);
    if (!selectedAddressId) {
      setSelectedAddress("");
      setSelectedCoordinates(null);
      setAddressDetails("");
    }
  };

  // Mark that there are unsaved changes in the form
  const setFormChanged = (changed = true) => {
    setHasUnsavedChanges(changed);
  };

  // Load addresses when the user changes
  useEffect(() => {
    if (user?.id && shouldRefetch) {
      fetchUserAddresses();
      // Después de cargar, reseteamos el flag para evitar recargas innecesarias
      setShouldRefetch(false);
    } else if (!user?.id) {
      setAddresses([]);
      setSelectedAddressId(null);
      // Limpiar estado relacionado con direcciones cuando el usuario cierra sesión
      setSelectedAddress("");
      setSelectedCoordinates(null);
      setAddressDetails("");
      setImageAddress(null);
      setDeliveryFee(0);
    }
  }, [user?.id, shouldRefetch]);

  return {
    addresses,
    isLoading,
    selectedAddressId,
    isCreatingNew,
    hasUnsavedChanges,
    fetchUserAddresses,
    selectAddress,
    createAddress,
    deleteAddress,
    startCreatingAddress,
    cancelCreatingAddress,
    setFormChanged,
    deselectAddress,
    isSelectingAddress,
  };
};
